import React, { createContext, useCallback, useContext, useReducer, useRef } from "react";

const ToastContext = createContext(null);

const MAX_TOASTS = 5;
let nextId = 0;

const reducer = (state, action) => {
  switch (action.type) {
    case "ADD":
      return [action.toast, ...state].slice(0, MAX_TOASTS);
    case "REMOVE":
      return state.filter((t) => t.id !== action.id);
    case "CLEAR":
      return [];
    default:
      return state;
  }
};

export const ToastProvider = ({ children }) => {
  const [toasts, dispatch] = useReducer(reducer, []);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
    dispatch({ type: "REMOVE", id });
  }, []);

  const toast = useCallback(
    ({ message, type = "info", duration = 4000, action }) => {
      const id = ++nextId;
      dispatch({ type: "ADD", toast: { id, message, type, action } });
      if (duration > 0) {
        timers.current[id] = setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const success = useCallback((message, opts) => toast({ message, type: "success", ...opts }), [toast]);
  const error = useCallback((message, opts) => toast({ message, type: "error", duration: 6000, ...opts }), [toast]);
  const warning = useCallback((message, opts) => toast({ message, type: "warning", ...opts }), [toast]);
  const info = useCallback((message, opts) => toast({ message, type: "info", ...opts }), [toast]);

  return (
    <ToastContext.Provider value={{ toasts, toast, success, error, warning, info, dismiss }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};
